/**
 * Turning an analytics list item into an Aggregated Metrics table row.
 *
 * Kept out of the component so it can be unit tested: the component itself
 * pulls in the whole table feature and cannot be imported in jest.
 */

import { buildFleetExportColumns } from "../fleetPerformanceTableConfig";

// This list is grouped by battery, so both fields carry the platform's internal
// battery id. It must never reach a downloaded or emailed file.
const INTERNAL_ID_FIELDS = ["xLabel", "batteryID"];

// The org-facing device id (IMEI and the like) identifies each row in the file
// instead, so a battery with no bus number mapped is still told apart.
export const EXPORT_DEVICE_ID_FIELD = "deviceID";

/**
 * Download / email columns for the Aggregated Metrics list: the device id first,
 * then every resolved column in table order, never the internal battery id - even
 * when org meta configures a column on it.
 *
 * @param {object|Array} columnsConfig
 * @param {Array} columnOrder
 * @param {string} [deviceIdLabel] - the org's device id label (globalConfig.baseEntity.idLabel)
 */
export const buildAggregatedMetricsExportColumns = (columnsConfig, columnOrder, deviceIdLabel = "Device ID") => {
  const { columns } = buildFleetExportColumns(columnsConfig, columnOrder);
  const exportColumns = columns.filter((column) => !INTERNAL_ID_FIELDS.includes(column.dataIndex));
  const hasDeviceId = exportColumns.some(
    (column) => column.dataIndex === EXPORT_DEVICE_ID_FIELD || column.headerLabel === deviceIdLabel
  );
  if (!hasDeviceId) {
    exportColumns.unshift({ dataIndex: EXPORT_DEVICE_ID_FIELD, headerLabel: deviceIdLabel });
  }
  return { columns: exportColumns, columnOrder: exportColumns.map((column) => column.headerLabel) };
};

/** Metrics the table reads off metricsArray, each defaulting to a dash. */
export const AGGREGATED_METRICS_TYPES = [
  "AveragekWhPerKm",
  "MinkWhPerKm",
  "MaxkWhPerKm",
  "AveragekWhPerSoc",
  "MinkWhPerSoc",
  "MaxkWhPerSoc",
  "AverageKmPerSoc",
  "MinKmPerSoc",
  "MaxKmPerSoc",
  "AverageSOH",
  "TotalChargingDurationMinutes",
  "TotalDischargingDurationMinutes",
];

export const buildAggregatedMetricsRow = (item) => {
  const metricsMap = {};
  (item?.metricsArray || []).forEach((m) => {
    metricsMap[m.type] = m.value;
  });

  const metrics = AGGREGATED_METRICS_TYPES.reduce((acc, type) => {
    acc[type] = metricsMap[type] ?? "-";
    return acc;
  }, {});

  return {
    // Every attribute the request asked for, so a column an org configured
    // through metaInfo.FleetPerformance.table renders its value instead of an
    // empty cell. The named fields below carry the dash defaults the existing
    // columns expect and so must stay after the spread.
    ...(item?.metaInfo || {}),
    // Every metric the response carries, for the same reason: the select
    // resolver asks for each configured column's metric, and it has to land on
    // the row too. A missing value is a dash, like the named metrics below.
    ...Object.fromEntries(
      Object.entries(metricsMap).map(([type, value]) => [type, value ?? "-"])
    ),
    // Identity, so it comes after the spread: a metaInfo key of any of these
    // names would otherwise replace the row id the table keys rows by.
    id: item?.xLabel,
    key: item?.xLabel,
    batteryID: item?.xLabel,
    deviceID: item?.metaInfo?.deviceID ?? "",
    // Never a dash: the device-id renderer reads pingStatus to colour the
    // connectivity dot and calls toLowerCase() on it.
    pingStatus: item?.metaInfo?.pingStatus ?? null,
    licencePlatNumber: item?.metaInfo?.licencePlatNumber ?? "-",
    originCountry: item?.metaInfo?.originCountry ?? "-",
    originState: item?.metaInfo?.originState ?? "-",
    originCity: item?.metaInfo?.originCity ?? "-",
    originDepot: item?.metaInfo?.originDepot ?? "-",
    ...metrics,
  };
};
